import { useMutation } from '@tanstack/react-query';

import FilterBar from 'components/shared/filters/FilterBar';
import { FilterSection } from 'components/shared/filters/FilterSlideout';
import FilterValueBoolean from 'models/filterValues/FilterValueBoolean';
import GroupType from 'models/GroupType';
import { upsertInsightsFilter } from 'services/api/preferences';
import { InsightsFilterState as Filters, useInsightsActions, useInsightsStore } from 'stores/insightsStore';

import { useInsightsContext } from '../InsightsContainer';
import ProviderTypeDropdown from '../ProviderTypeDropdown';

import InsightsDateRangePicker from './InsightsDateRangePicker';

type InsightsFilterBarProps = {
  activeFilters: Filters;
  filterSections: FilterSection[];
};

/**
 * @deprecated Use InsightsTabsFilterBar for new insights pages.
 * This filter bar is specific to the legacy Insights dashboard.
 */
const InsightsFilterBar = ({ filterSections, activeFilters }: InsightsFilterBarProps) => {
  const { profile } = useInsightsContext();
  const { selectedGroupType } = useInsightsStore();
  const { setBreadcrumbs, restoreInitialSnapshot, setSelectedFilters, setSelectedGroupType } = useInsightsActions();

  const { mutate } = useMutation({
    mutationFn: upsertInsightsFilter,
  });

  const handleChangeGroupType = (groupType: GroupType) => {
    setSelectedGroupType(groupType);

    restoreInitialSnapshot();

    if (groupType.apiName !== selectedGroupType?.apiName) {
      mutate({
        clientId: profile.actingClient.id,
        value: {
          providerType: groupType.apiName,
        },
      });
    }
  };

  const handleClearFilters = () => {
    Object.entries(activeFilters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        setSelectedFilters({ [key]: [] });
      } else if (value instanceof FilterValueBoolean) {
        setSelectedFilters({ [key]: new FilterValueBoolean({ name: key, value: false }) });
      }
    });

    setBreadcrumbs([]);
  };

  return (
    <FilterBar
      showSearchInput={false}
      applyFilters={(filters: Filters) => {
        setSelectedFilters(filters);
      }}
      filters={activeFilters}
      showResults={false}
      filterSections={filterSections}
      onClearFilters={handleClearFilters}
      title={
        <>
          {profile && selectedGroupType && (
            <ProviderTypeDropdown
              enabledProviderTypes={profile.enabledProviderTypes}
              key={selectedGroupType.id}
              initialSelectedProviderType={selectedGroupType}
              onChange={handleChangeGroupType}
            />
          )}
        </>
      }
      callToActionShowMinWidth={800}
      callToAction={
        <InsightsDateRangePicker
          gtmPrefix='insights'
          filters={activeFilters}
          setSelectedFilters={setSelectedFilters}
          fullWidthOnMobile={false}
        />
      }
    />
  );
};

export default InsightsFilterBar;
